// What released what. The trace records a wait's start and end and every write to
// every flag, but not who released whom; this module joins them.
//
// The rule (checked against the sample runs, where it resolves every stalled wait): the
// releaser of a wait on flag K parked at t0 and released at t1 is the LAST write to K
// with t0 < at <= t1. Never `at == t1` exactly -- a core resumes on its own next cycle
// edge -- so the window, not equality, is the match. If a DMA's completion credited K
// at that very `at`, the DMA is the releaser; if the write carries a firmware command
// id, the host is; otherwise a TensorCore bundle issued within a cycle of `at` with a
// sync-capable slot is the likely writer; failing all, a core that leaves no bundles
// (a SparseCore) wrote it.
//
// The attribution is in two halves, because the halves cost differently. Finding the
// write, and saying whether it was the host's or a DMA's, is two binary searches --
// that is done for every wait when the run opens, and it is all that the classification
// below, the stalls list and the host's anchors need. Finding *which* core wrote it
// walks every stream of the chip, and on a v7x slice, where 128 SparseCore tiles
// release each other's step synchronisation a quarter of a million times, that is
// seconds of work nothing shows until a tile row is opened. So a tile's waits keep
// their write and are attributed one stream at a time, on demand: `links.releaserOf(w)`
// and `links.resolveRow(rowKey)`.
//
// Neither half keeps a record: what it found is written into columns over the waits --
// how it was released, the write, and the DMA, host command or core behind it -- and
// `releaserOf` builds the record a tooltip or a panel reads from those, one at a time.

import { UNIT_BIT } from "./isa.js";
import { flagIdOf } from "./fmt.js";
import { fmtPs, lowerIndex, upperIndex } from "./dom.js";

const SYNC_MASK = UNIT_BIT.sync | UNIT_BIT.misc | UNIT_BIT.dma;

// How a wait was released, as `waitHow` holds it. A DMA is two codes because the two
// ways of finding one say different things about it: a write that names its DMA names
// the chip the DMA ran on, a credit landing at the stamp is the flag's own chip.
const OPEN = 0, ALREADY = 1, NONE = 2, DMA_NAMED = 3, DMA_CREDIT = 4, HOST = 5, CORE = 6, UNKNOWN = 7, DEFER = 8;
const HOW = ["open", "already", "none", "dma", "dma", "host", "core", "unknown", "deferred"];
const isDma = (how) => how === DMA_NAMED || how === DMA_CREDIT;

// The row a DMA was issued from.
const dmaRow = (d) => (d.byKind === "tc" ? `c${d.chip}t${d.byCi}` : d.byKind === "sc" ? `c${d.chip}s${d.byCi}` : `c${d.chip}host`);

export function rowKeyOf(chip, core, ci, tile) {
  if (core === "TensorCore") return `c${chip}t${ci}`;
  if (core === "SparseCore") return `c${chip}s${ci}` + (tile == null ? "" : `t${tile}`);
  if (core === "HostInterface") return `c${chip}host`;
  return `c${chip}other`;
}

// A wait whose attribution waits for someone to ask: one of a SparseCore tile's own.
const deferrable = (w) => w.core === "SparseCore" && w.tile != null;

// Everything below is an array over the flags, not a hash over the writes: a slice
// writes a million times to a couple of thousand flags, and the difference between the
// two is most of a run's opening time. `number` gives every flag id met -- written or
// waited on -- a dense number, and the row it belongs to one string, the first time it
// is seen: a flag id is its row's id times the flags a row owns, plus the index (fmt.js),
// so naming the row once per flag names it once per row.
function keySpace() {
  const of = new Map(), rows = [];
  return {
    rows,
    idOf: (key) => of.get(key),
    number(keys, recs) {
      const ids = new Int32Array(recs.length);
      for (let i = 0; i < recs.length; i++) {
        let id = of.get(keys[i]);
        if (id === undefined) {
          const x = recs[i];
          of.set(keys[i], id = rows.length);
          rows.push(rowKeyOf(x.chip, x.core, x.ci, x.tile));
        }
        ids[i] = id;
      }
      return ids;
    },
  };
}

// A counting sort into one flat array: the members of group k are `out[off[k]]` up to
// `out[off[k + 1]]`, in the order they were met -- which is the trace's, and so already
// `by` order except where two threads interleaved. Only the groups that are out of
// order are sorted, and they are few.
function groupBy(gid, n, by) {
  const off = new Int32Array(n + 1);
  for (let i = 0; i < gid.length; i++) off[gid[i] + 1]++;
  for (let k = 0; k < n; k++) off[k + 1] += off[k];
  const cur = off.slice(0, n), out = new Int32Array(gid.length);
  for (let i = 0; i < gid.length; i++) out[cur[gid[i]]++] = i;
  for (let k = 0; k < n; k++) {
    const g = out.subarray(off[k], off[k + 1]);
    for (let j = 1; j < g.length; j++) if (by[g[j]] < by[g[j - 1]]) { g.sort((x, y) => by[x] - by[y] || x - y); break; }
  }
  return { off, out };
}

export function buildLinks(model) {
  const waits = model.waits, flags = model.flags;
  const space = keySpace();
  const flagKeyId = space.number(model.flagKey, flags);
  const waitKeyId = space.number(model.waitKey, waits);
  const rowOfKey = space.rows, nKeys = rowOfKey.length;
  const rowOf = (w) => rowOfKey[waitKeyId[w.i]];

  // Each flag's writes, by stamp, and their stamps: two views on one flat array each.
  const byKey = groupBy(flagKeyId, nKeys, model.flagAt);
  const keyAt = new Float64Array(flags.length);
  for (let i = 0; i < flags.length; i++) keyAt[i] = model.flagAt[byKey.out[i]];
  const flagsOfKey = new Array(nKeys), atsOfKey = new Array(nKeys);
  for (let k = 0; k < nKeys; k++) {
    flagsOfKey[k] = byKey.out.subarray(byKey.off[k], byKey.off[k + 1]);
    atsOfKey[k] = keyAt.subarray(byKey.off[k], byKey.off[k + 1]);
  }

  const creditIndex = new Array(nKeys); // key -> [{dma, credit}] sorted by dma.end
  for (const d of model.dmas) {
    for (const cr of d.credits || []) {
      // A credit to a flag nothing ever wrote cannot have released anything: it is
      // only ever looked up against a write that was found.
      const k = space.idOf(flagIdOf(cr.chip, cr.core, cr.ci, cr.tile, cr.idx));
      if (k === undefined) continue;
      (creditIndex[k] || (creditIndex[k] = [])).push({ dma: d, credit: cr });
    }
  }
  for (const a of creditIndex) if (a) a.sort((x, y) => x.dma.end - y.dma.end);

  // Everything a firmware command caused, by chip:id.
  const byCmd = new Map();
  const cmdRec = (chip, id) => {
    const k = chip + ":" + id;
    let r = byCmd.get(k);
    if (!r) { r = { host: model.fwByCmd.get(k) || null, dispatch: null, dmas: [], flags: [], sc: null }; byCmd.set(k, r); }
    return r;
  };
  for (const d of model.dispatches) if (d.cmd != null) cmdRec(d.chip, d.cmd).dispatch = d;
  for (const d of model.dmas) if (d.cmd != null) cmdRec(d.chip, d.cmd).dmas.push(d);
  for (const f of model.flags) if (f.cmd != null) cmdRec(f.chip, f.cmd).flags.push(f);

  const dmaByIssuer = new Map();
  for (const d of model.dmas) {
    const k = dmaRow(d);
    if (!dmaByIssuer.has(k)) dmaByIssuer.set(k, []);
    dmaByIssuer.get(k).push(d.i);
  }
  for (const a of dmaByIssuer.values()) a.sort((x, y) => (model.dmas[x].at || 0) - (model.dmas[y].at || 0));

  const stallsByRow = new Map();
  for (const s of model.stalls) {
    const k = s.core.key;
    if (!stallsByRow.has(k)) stallsByRow.set(k, []);
    stallsByRow.get(k).push(s.i);
  }
  for (const a of stallsByRow.values()) a.sort((x, y) => model.stalls[x].t0 - model.stalls[y].t0);

  // The waits and the writes of each row, by time. Both are grouped through the key
  // space: a row is named once per flag rather than once per record.
  const rowNum = new Map();                            // row key -> dense number
  const rowNumOfKey = new Int32Array(nKeys);
  for (let k = 0; k < nKeys; k++) {
    let r = rowNum.get(rowOfKey[k]);
    if (r === undefined) rowNum.set(rowOfKey[k], r = rowNum.size);
    rowNumOfKey[k] = r;
  }
  const rowsOf = (keyIds, by) => {
    const gid = new Int32Array(keyIds.length);
    for (let i = 0; i < keyIds.length; i++) gid[i] = rowNumOfKey[keyIds[i]];
    const g = groupBy(gid, rowNum.size, by);
    const out = new Map();
    for (const [k, r] of rowNum) if (g.off[r + 1] > g.off[r]) out.set(k, g.out.subarray(g.off[r], g.off[r + 1]));
    return out;
  };
  const waitT0 = new Float64Array(waits.length);
  for (let i = 0; i < waits.length; i++) waitT0[i] = waits[i].t0;
  const waitsByRow = rowsOf(waitKeyId, waitT0);
  const flagsByRow = rowsOf(flagKeyId, model.flagAt);

  const links = {
    flagsOfKey, atsOfKey, creditIndex, byCmd, dmaByIssuer, waitsByRow, flagsByRow, stallsByRow,
    howCount: {},
    // How each wait was released, the write that did it, and the DMA, host command or
    // core behind it where there was one (-1 for none). What each of those released in
    // turn is the reverse of these, counted out only when a panel asks (`releasedWaits`).
    waitHow: new Uint8Array(waits.length),
    waitFlag: new Int32Array(waits.length).fill(-1),
    waitDma: new Int32Array(waits.length).fill(-1),
    waitHost: new Int32Array(waits.length).fill(-1),
    waitCore: new Int32Array(waits.length).fill(-1),
    releasedIx: new Map(),
    deferredRows: new Map(),
  };

  // The cheap half, for every wait. A deferred one leaves its write behind and no
  // releaser; `releaserOf` finishes it, a whole tile stream at a time.
  const defer = new Array(rowNum.size);
  for (let i = 0; i < waits.length; i++) {
    const w = waits[i];
    const how = links.waitHow[i] = firstHalf(links, model, w, waitKeyId[i]);
    if (how === DEFER) {
      const rn = rowNumOfKey[waitKeyId[i]];
      if (!defer[rn]) links.deferredRows.set(rowOfKey[waitKeyId[i]], defer[rn] = []);
      defer[rn].push(i);
    }
    links.howCount[HOW[how]] = (links.howCount[HOW[how]] || 0) + 1;
    // What kind of idle a wait is: waiting for the host (it released it, its DMA did,
    // or nothing in the trace did) or for another core's work. The two are drawn apart.
    // A deferred wait is one of the second kind whichever core wrote its flag.
    w.cls = how === DEFER || how === CORE || how === UNKNOWN
      || (isDma(how) && model.dmas[links.waitDma[i]].byKind !== "host") ? "core" : "host";
  }
  for (const s of model.stalls) s.cls = "core";

  // ... and transitively: a wait released by a core that was itself mostly idle for
  // the host (or by that core's DMA) was waiting for the host at one remove. The rule:
  // more than half of the wait overlaps the releasing core's own host idling. Rows
  // whose host spans grew are re-checked until nothing changes. A deferred wait joins
  // the pass when its stream is attributed, which is why `resolveRow` settles again.
  // A releasing row need not own a flag or park on one, so the numbering the grouping
  // above gave the key space is extended as the pass meets one.
  const rowIdOf = (key) => {
    let r = rowNum.get(key);
    if (r === undefined) rowNum.set(key, r = rowNum.size);
    return r;
  };
  const relRow = new Int32Array(model.waits.length).fill(-1);
  const byRelRow = [];              // releasing row -> the waits it released
  const spans = [];                 // row -> {v: [t0, t1, ...] of its host waits, n, starts}
  const relate = (i) => {
    const how = links.waitHow[i];
    let key = null;
    if (how === CORE) key = model.cores[links.waitCore[i]].key;
    else if (isDma(how)) { const d = model.dmas[links.waitDma[i]]; if (d.byKind !== "host") key = dmaRow(d); }
    if (key === null) return;
    const r = relRow[i] = rowIdOf(key);
    (byRelRow[r] || (byRelRow[r] = [])).push(i);
  };
  const addSpan = (i) => {
    const w = model.waits[i], k = rowIdOf(rowOf(w));
    let s = spans[k];
    if (!s) spans[k] = s = { v: new Float64Array(32), n: 0, starts: null };
    if (s.n + 2 > s.v.length) { const b = new Float64Array(s.v.length * 2); b.set(s.v); s.v = b; }
    s.v[s.n++] = w.t0; s.v[s.n++] = w.open ? model.tEnd : w.t1;
    s.starts = null;
    return k;
  };
  const overlap = (k, a, b) => {
    const s = spans[k];
    if (!s) return 0;
    if (!s.starts) {                // the pairs, sorted by start the first time asked
      const n = s.n / 2, ord = new Int32Array(n);
      for (let i = 0; i < n; i++) ord[i] = i;
      ord.sort((x, y) => s.v[2 * x] - s.v[2 * y]);
      const v = new Float64Array(s.n), starts = new Float64Array(n);
      for (let i = 0; i < n; i++) { starts[i] = v[2 * i] = s.v[2 * ord[i]]; v[2 * i + 1] = s.v[2 * ord[i] + 1]; }
      s.v = v; s.starts = starts;
    }
    let sum = 0, i = Math.max(0, lowerIndex(s.starts, a));
    for (; i < s.starts.length; i++) { const s0 = s.v[2 * i], s1 = s.v[2 * i + 1]; if (s1 <= a) continue; if (s0 >= b) break; sum += Math.min(s1, b) - Math.max(s0, a); }
    return sum;
  };
  const flip = (i) => {
    const w = model.waits[i];
    if (w.cls !== "core" || relRow[i] < 0) return false;
    const t1 = w.open ? model.tEnd : w.t1;
    if (!(t1 > w.t0) || overlap(relRow[i], w.t0, t1) <= 0.5 * (t1 - w.t0)) return false;
    w.cls = "host";
    return true;
  };
  const settle = (rows) => {
    let dirty = rows;
    for (let pass = 0; pass < 16 && dirty.size; pass++) {
      const next = new Set();
      for (const k of dirty) for (const i of byRelRow[k] || []) if (flip(i)) next.add(addSpan(i));
      dirty = next;
    }
  };
  for (let i = 0; i < waits.length; i++) if (links.waitHow[i] !== DEFER) relate(i);
  const dirty = new Set();
  for (let i = 0; i < waits.length; i++) if (waits[i].cls === "host") dirty.add(addSpan(i));
  settle(dirty);

  // Attribute one row's deferred waits -- a tile stream at a time, which is how they
  // are looked at -- and let the transitive pass run again over the edges that adds.
  links.resolveRow = (rowKey) => {
    const ids = links.deferredRows.get(rowKey);
    if (!ids) return;
    links.deferredRows.delete(rowKey);
    const rows = new Set();
    for (const i of ids) {
      const how = links.waitHow[i] = secondHalf(links, model, model.flags[links.waitFlag[i]], i);
      links.howCount[HOW[how]] = (links.howCount[HOW[how]] || 0) + 1;
      links.howCount.deferred--;
      relate(i);
    }
    for (const i of ids) if (flip(i)) rows.add(addSpan(i));
    settle(rows);
  };
  links.resolveAll = () => { for (const k of [...links.deferredRows.keys()]) links.resolveRow(k); };
  links.releaserOf = (w) => {
    if (links.waitHow[w.i] === DEFER) links.resolveRow(rowOf(w));
    return record(links, model, w.i);
  };

  // A core is synchronised when a flag ever joined its clock to another thread's:
  // a wait of its own released by someone else, or a write of its own releasing
  // someone else's wait. Only a trace whose host is not placed for us needs the
  // question asked, and such a trace has no tile streams to defer.
  for (const c of model.cores) c.unsynced = c.n > 0 && !model.hostTimed;
  if (!model.hostTimed) {
    links.resolveAll();
    for (const w of model.waits) {
      const how = links.waitHow[w.i];
      if (how === OPEN || how === ALREADY || how === NONE) continue;
      const rc = how === CORE ? model.cores[links.waitCore[w.i]] : null;
      const c = model.coreByKey.get(rowOf(w));
      if (c && rc !== c) c.unsynced = false;
      if (rc) rc.unsynced = false;
      if (isDma(how)) { const dma = model.dmas[links.waitDma[w.i]]; const d = model.coreByKey.get(dma.byKind === "tc" ? `c${dma.chip}t${dma.byCi}` : `c${dma.chip}s${dma.byCi}`); if (d) d.unsynced = false; }
    }
  }
  return links;
}

// The last write to flag `kid` with t0 < at <= t1 -- the one that released the wait --
// or null. Two binary searches and no list: this runs once per wait in the run.
function lastWriteIn(links, model, kid, t0, t1) {
  const ats = links.atsOfKey[kid];
  const lo = upperIndex(ats, t0 + 1e-9);          // first at > t0
  const hi = lowerIndex(ats, t1);                 // the last at <= t1
  return hi >= lo ? model.flags[links.flagsOfKey[kid][hi]] : null;
}

// The half every wait gets: the write that released it, and the releaser itself where
// the write says so. `DEFER` where only a walk of the streams would tell. What it found
// goes into the columns; the code is what the wait is now attributed as.
function firstHalf(links, model, w, kid) {
  const i = w.i;
  if (w.open) return OPEN;
  const f = lastWriteIn(links, model, kid, w.t0, w.t1);
  if (!f) {
    if (w.stallPs === 0) {
      const j = lowerIndex(links.atsOfKey[kid], w.t0);
      if (j >= 0) links.waitFlag[i] = links.flagsOfKey[kid][j];
      return ALREADY;
    }
    return NONE;
  }
  links.waitFlag[i] = f.i;
  // The write names the DMA whose credit it was (format 2), wherever that DMA ran.
  if (f.dma != null) {
    const dma = model.dmaById.get(f.dma);
    if (dma) { links.waitDma[i] = dma.i; return DMA_NAMED; }
  }
  // The host, through a firmware command.
  if (f.cmd != null) {
    const host = model.fwByCmd.get(f.chip + ":" + f.cmd);
    links.waitHost[i] = host ? host.i : -1;
    return HOST;
  }
  // A DMA's credit landing.
  const crs = links.creditIndex[kid];
  if (crs) {
    for (const { dma, credit } of crs) {
      if (dma.end === f.at && (f.op !== "incr" || credit.delta == null || credit.delta === f.delta)) {
        links.waitDma[i] = dma.i;
        return DMA_CREDIT;
      }
    }
  }
  return deferrable(w) ? DEFER : secondHalf(links, model, f, i);
}

// The dear half: which core wrote `f`. A core's own sync slot, within a cycle of the
// stamp -- the flag's chip first, the rest only if nothing there wrote it.
function secondHalf(links, model, f, wi) {
  let best = null;
  const tryCores = (cores) => {
    for (const c of cores) {
      const bi = model.bundleAt(c, f.at);
      if (bi < 0) continue;
      const dt = f.at - c.t[bi];
      if (dt < c.cyclePs && (c.umask[bi] & SYNC_MASK)) {
        const score = (c.chip === f.chip ? 2 : 0) + ((c.umask[bi] & UNIT_BIT.sync) ? 1 : 0);
        if (!best || score > best.score) best = { core: c, bi, score };
      }
    }
  };
  if (!links.coresByChip) { links.coresByChip = new Map(); for (const c of model.cores) { if (!links.coresByChip.has(c.chip)) links.coresByChip.set(c.chip, []); links.coresByChip.get(c.chip).push(c); } }
  tryCores(links.coresByChip.get(f.chip) || []);
  if (!best) tryCores(model.cores.filter((c) => c.chip !== f.chip));
  if (best) {
    links.waitCore[wi] = best.core.idx;
    return CORE;
  }
  return UNKNOWN;
}

// The releaser as everything that shows one reads it, built from the columns. Nothing
// holds it: a tooltip, the detail panel and a row of the stalls list each want one.
// `bi` is the bundle the write was attributed to, which is the write's stamp on the
// releasing core -- the same search `secondHalf` made.
function record(links, model, i) {
  const how = links.waitHow[i], fi = links.waitFlag[i], f = fi >= 0 ? model.flags[fi] : null;
  switch (how) {
    case OPEN:
      return { how: "open", text: "still waiting when the trace ended" };
    case ALREADY:
      return { how: "already", flag: f, text: "the flag already satisfied the wait" + (f ? ` (last written at ${fmtPs(f.at)})` : "") };
    case NONE:
      return { how: "none", text: "no write to this flag inside the wait: the releasing write is not in the trace" };
    case DMA_NAMED: case DMA_CREDIT: {
      const dma = model.dmas[links.waitDma[i]];
      const from = how === DMA_NAMED && dma.chip !== f.chip ? ` (from chip ${dma.chip})` : "";
      return { how: "dma", flag: f, dma, text: `DMA #${dma.id} landing${from}`, fromRow: dmaRow(dma) };
    }
    case HOST: {
      const host = links.waitHost[i] >= 0 ? model.host[links.waitHost[i]] : null;
      return { how: "host", flag: f, host, text: `${wrote(f)} by the host (firmware command #${f.cmd}${host ? " " + host.kind : ""})`, fromRow: `c${f.chip}host` };
    }
    case CORE: {
      const core = model.cores[links.waitCore[i]], bi = model.bundleAt(core, f.at);
      return { how: "core", flag: f, core, bi, text: `${wrote(f)} by ${core.label} at pc ${core.pc[bi]}`, fromRow: core.key };
    }
    default:
      // No bundle claims the write: a core that left none in the trace made it.
      return { how: "unknown", flag: f, text: `${wrote(f)} at ${fmtPs(f.at)} by a core that left no bundles in the trace`, fromRow: f.core === "SparseCore" ? `c${f.chip}s${f.ci}` : null };
  }
}

// What a write did to the flag, as a sentence starts: "set to 3" or "incremented (+1)".
function wrote(f) {
  return f.op === "incr" ? `incremented (${f.delta >= 0 ? "+" : ""}${f.delta})` : `set to ${f.new}`;
}

// The waits a flag write / DMA / host command released. Every wait names its releaser
// as it is attributed; the reverse is counted out here the first time a panel asks,
// because on a slice it is a quarter of a million entries that nothing else reads.
export function releasedWaits(links, model, kind, i) {
  let ix = links.releasedIx.get(kind);
  if (!ix) {
    const of = kind === "flag" ? links.waitFlag : kind === "dma" ? links.waitDma : links.waitHost;
    const n = (kind === "flag" ? model.flags : kind === "dma" ? model.dmas : model.host).length;
    const off = new Int32Array(n + 1);
    for (let j = 0; j < of.length; j++) if (of[j] >= 0) off[of[j] + 1]++;
    for (let k = 0; k < n; k++) off[k + 1] += off[k];
    const cur = off.slice(0, n), ids = new Int32Array(off[n]);
    for (let j = 0; j < of.length; j++) if (of[j] >= 0) ids[cur[of[j]]++] = j;
    links.releasedIx.set(kind, ix = { off, out: ids });
  }
  const out = [];
  for (let j = ix.off[i]; j < ix.off[i + 1]; j++) out.push(model.waits[ix.out[j]]);
  return out;
}
